import React from "react";
import { Link } from "react-router-dom";

const stages = [
  {
    number: "01",
    title: "Choose media in Nori Studio",
    body: "A creator in a tenant's app opens Nori Studio and selects an image or video to submit as a reel.",
    detail: "The tenant app provides the creator experience. The backend remains responsible for authorizing the upload and associating it with the correct tenant.",
  },
  {
    number: "02",
    title: "Upload directly to tenant storage",
    body: "The backend returns a signed upload link scoped to that tenant's storage folder. The selected file uploads straight to storage.",
    detail: "Keep storage credentials server-side. Signed links should be short-lived and scoped to the intended object and operation.",
  },
  {
    number: "03",
    title: "Route video straight to review",
    body: "After a video upload completes, attach the media to its reel and place the reel in the tenant admin's review queue.",
    detail: "No image-generation job is needed for a video submission.",
  },
  {
    number: "04",
    title: "Generate video from an image",
    body: "For an image submission, the backend sends an image-generation service signed read and upload links plus a webhook URL. When processing finishes, the generated video is attached to the reel and the reel enters review.",
    detail: "The processing callback should be associated with the original tenant and reel. Production callback authentication, retry behavior, and failure handling need to be agreed in implementation.",
  },
  {
    number: "05",
    title: "Review, approve, or reject",
    body: "A tenant admin reviews the reel before publication. Approval sets viewer points and creator points, with an optional view budget. Rejection includes a note for the creator.",
    detail: "Nothing is public until an admin approves it. Reward values and moderation rules are configured for the tenant.",
  },
  {
    number: "06",
    title: "Reward both sides of a view",
    body: "Once the reel is live, each eligible rewarded view pays both the viewer and the creator according to the approved reward rules and any remaining view budget.",
    detail: "Reward limits and abuse controls should be defined before a pilot; the resulting behavior needs measurement against reward and moderation costs.",
  },
];

const quickStart = [
  {
    number: "01",
    title: "Install what you use",
    body: "Every module builds on the core client. Add the ones your app needs.",
    code: "npm install @getnori/core @getnori/feed @getnori/commerce @getnori/rewards react-native-video",
  },
  {
    number: "02",
    title: "Mint a viewer token on your backend",
    body: "Your backend returns a short-lived token for the signed-in user. The SDK asks for it through getViewerToken and refreshes it when the API rejects it. The publishable key (pk_live_...) identifies your organization and is safe to ship in the app.",
  },
  {
    number: "03",
    title: "Create the client once",
    body: "Pass the publishable key and a function that fetches the token.",
    code: `import { createNoriClient } from "@getnori/core";

export const nori = createNoriClient({
  publishableKey: "pk_live_...",
  getViewerToken: async () => {
    const res = await fetch("https://your-api.example.com/nori-token", { method: "POST" });
    return (await res.json()).token;
  },
});`,
  },
  {
    number: "04",
    title: "Render the feed",
    body: "Add product cards and rewards as overlays. Rewards are decided on the server; the SDK reports views and shows the result.",
    code: `import { NoriFeed } from "@getnori/feed";
import { ProductStrip } from "@getnori/commerce";
import { PointsBadge, RewardToasts } from "@getnori/rewards";

<View style={{ flex: 1 }}>
  <NoriFeed
    client={nori}
    renderOverlay={({ reel, positionMs }) => (
      <ProductStrip client={nori} reel={reel} positionMs={positionMs}
        onAddToCart={(p) => cart.add(p.sku)} />
    )}
  />
  <PointsBadge client={nori} style={{ position: "absolute", top: 56, right: 16 }} />
  <RewardToasts client={nori} />
</View>`,
  },
];

const modules = [
  { name: "@getnori/core", does: "API client, viewer auth, event tracking", available: true },
  { name: "@getnori/feed", does: "Creator reels and local content in the app", available: true },
  { name: "@getnori/commerce", does: "Product cards and add to cart in the video", available: true },
  { name: "@getnori/rewards", does: "Points for watching, sharing and showing up", available: true },
  { name: "@getnori/studio", does: "Creators add reels; admins review them", available: true },
  { name: "@getnori/creator", does: "Recruit, brief and pay local creators", available: false },
  { name: "@getnori/events", does: "Store events, launches and community moments", available: false },
  { name: "@getnori/analytics", does: "Holdout-tested lift from content to purchase", available: false },
];

export default function DocsPage() {
  return (
    <main className="docs-page">
      <section className="docs-hero page-shell">
        <p className="eyebrow">NORI DOCS</p>
        <h1>Get started<br /><em>with the Nori SDK.</em></h1>
        <p>Install the SDK, connect your app, and put a reel feed on screen. Then see how an upload moves from creator to reviewed reel.</p>
        <div className="docs-notice"><strong>Pre-1.0</strong><span>The SDK is still being packaged and the API may change. Publishable keys and tenant setup come from Nori; <Link to="/contact">get in touch</Link> to start.</span></div>
      </section>

      <section className="docs-start" id="get-started">
        <div className="page-shell">
          <div className="docs-section-heading"><p className="eyebrow">DEVELOPER QUICK START</p><h2>From install to a working feed.</h2></div>
          <ol className="docs-steps">
            {quickStart.map((step) => (
              <li className="docs-step" key={step.number}>
                <span className="docs-stage-number">{step.number}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                  {step.code && <pre className="code-block"><code>{step.code}</code></pre>}
                </div>
              </li>
            ))}
          </ol>
          <div className="docs-modules">
            <div><p className="eyebrow">AVAILABLE</p><ul>{modules.filter((m) => m.available).map((m) => <li key={m.name}><code>{m.name}</code><span>{m.does}</span></li>)}</ul></div>
            <div><p className="eyebrow">PLANNED</p><ul>{modules.filter((m) => !m.available).map((m) => <li key={m.name}><code>{m.name}</code><span>{m.does}</span></li>)}</ul></div>
          </div>
          <p className="caveat docs-start-note">React Native first; native iOS (Swift) and Android (Kotlin) follow. Modules depend only on <code>@getnori/core</code>, so install the ones you use. On the web, import <code>NoriReelsWeb</code> from <code>@getnori/feed/dom</code>.</p>
        </div>
      </section>

      <section className="docs-flow-section">
        <div className="page-shell">
          <div className="docs-section-heading"><p className="eyebrow">TENANT ONBOARDING: UPLOAD, PROCESS, REVIEW, REWARD</p><h2>One flow. Two media paths.</h2></div>
          <div className="docs-flow-map" aria-label="Media processing paths">
            <span>Creator selects media</span><span>Signed tenant upload</span><span className="docs-branch">Video: review<br />Image: generation, then review</span><span>Admin decision</span><span>Viewer + creator rewards</span>
          </div>
          <ol className="docs-stages">
            {stages.map((stage) => (
              <li className="docs-stage" key={stage.number}>
                <span className="docs-stage-number">{stage.number}</span>
                <div className="docs-stage-copy"><h3>{stage.title}</h3><p>{stage.body}</p><p className="docs-stage-detail">{stage.detail}</p></div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="docs-boundaries">
        <div className="page-shell docs-boundaries-grid">
          <div><p className="eyebrow">BEFORE A PILOT</p><h2>Make the tenant boundary explicit.</h2></div>
          <div><p>Confirm tenant identity, storage paths, allowed media types, link expiration, review roles, reward budgets, and the media-processing completion callback with the implementation team.</p><p>Define content rights, retention, privacy notices, abuse handling, and operational ownership before creators submit real content. A tenant integration still requires configuration and implementation.</p></div>
        </div>
      </section>
    </main>
  );
}